import { createListenerMiddleware, isAnyOf } from '@reduxjs/toolkit'
import debounce from 'lodash.debounce'

import { projectDataThunks } from 'store/features/projectData/thunks'
import { projectThunks } from 'store/features/project/thunks'
import projectDataSlice from 'store/features/projectData/slice'

import projectSlice from 'store/features/project/slice'
import { IProjectData } from 'types/projectData'
import { TRootState } from 'store/types'

import { TAppDispatch } from 'store/types'
import { NEW_PROJECT_NAME } from 'consts'

const projectActions = projectSlice.actions
const projectDataActions = projectDataSlice.actions

const projectListenerMiddleware = createListenerMiddleware<
  TRootState,
  TAppDispatch
>()

// This middleware is used to create a project if it doesn't exist
projectListenerMiddleware.startListening({
  matcher: isAnyOf(
    projectDataActions.updateUploadedMediaItems,
    projectDataActions.uploadFromMediaLibrary,
    projectDataActions.addMediaItems,
    projectDataActions.addMultiLineAudios,
    projectDataActions.updateTtsAudios
  ),
  effect: async (_, listenerApi) => {
    const state = listenerApi.getState()

    if (Object.keys(state.project.data).length === 0) {
      await listenerApi.dispatch(projectThunks.createProject(NEW_PROJECT_NAME))
    }
  }
})

// This debounced function is used to update the project data
const debouncedUpdateProject = debounce(
  async (dispatch: TAppDispatch, projectId: string, data: IProjectData) => {
    await dispatch(projectDataThunks.patchProjectData(projectId, data))
  },
  1000
)

// This listener is used to update the project data
projectListenerMiddleware.startListening({
  matcher: isAnyOf(
    projectActions.setCreatedProject,
    projectDataActions.uploadFromMediaLibrary,
    projectDataActions.updateUploadedMediaItems,
    projectDataActions.removeVisualMediaItem,
    projectDataActions.splitVisualMediaItem,
    projectDataActions.splitAudioItem,
    projectDataActions.addMediaItems,
    projectDataActions.setMediaItems,
    projectDataActions.updateMediaItem,
    projectDataActions.updateAudioItems,
    projectDataActions.updateTtsAudios,
    projectDataActions.addMultiLineAudios,
    projectDataActions.setMultilineAudios,
    projectDataActions.updateUploadedAudioFilePaths,
    projectDataActions.removeAudiosFromProject,
    projectDataActions.duplicateAudioItem,
    projectDataActions.updateSoundVersion,
    projectDataActions.updateActiveAudioVolume,
    projectDataActions.undo,
    projectDataActions.redo,
    projectDataActions.removeTimelineSelectedItems,
    projectDataActions.updateTimelineItems,
    projectDataActions.updateMediaItemWithoutUndoSave
  ),
  effect: async (_, listenerApi) => {
    const state = listenerApi.getState()
    const projectId = state.project.data.id

    if (projectId) {
      debouncedUpdateProject(
        listenerApi.dispatch,
        `${projectId}`,
        state.projectData.present
      )
    }
  }
})

export const projectMiddleware = projectListenerMiddleware.middleware
